import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import SellerPayoutPreview from '../SellerPayoutPreview'
import BuilderStepHead from './BuilderStepHead'

export type BuilderPricingTier = 'quick' | 'recommended' | 'patient'

export type BuilderPriceSuggestion = {
  quick_sale_eur: number
  recommended_eur: number
  patient_eur: number
}

type Props = {
  isBasicPhone: boolean
  price: string
  onPriceChange: (value: string) => void
  suggestion: BuilderPriceSuggestion | null
  pricingLoading: boolean
  pricingError: string
  hasCoverPhoto: boolean
  condition?: string
  onApplyTier: (tier: BuilderPricingTier) => void
  isGreedy: boolean
  paymentsEnabled: boolean
}

function tierFromPrice(price: string, suggestion: BuilderPriceSuggestion | null): BuilderPricingTier | null {
  if (!suggestion) return null
  const p = parseFloat(price.replace(',', '.'))
  if (!Number.isFinite(p)) return 'recommended'
  const eps = 0.015
  if (Math.abs(p - suggestion.quick_sale_eur) < eps) return 'quick'
  if (Math.abs(p - suggestion.patient_eur) < eps) return 'patient'
  if (Math.abs(p - suggestion.recommended_eur) < eps) return 'recommended'
  return null
}

function IconSparkles() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <path d="M12 3l1.2 3.8L17 8l-3.8 1.2L12 13l-1.2-3.8L7 8l3.8-1.2L12 3z" strokeLinejoin="round" />
      <path d="M5 14l.8 2.2L8 17l-2.2.8L5 20l-.8-2.2L2 17l2.2-.8L5 14z" strokeLinejoin="round" />
    </svg>
  )
}

function IconInfo() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 10v6M12 7h.01" strokeLinecap="round" />
    </svg>
  )
}

function formatEur(amount: number) {
  return `€${amount.toFixed(2)}`
}

export default function BuilderPriceStep({
  isBasicPhone,
  price,
  onPriceChange,
  suggestion,
  pricingLoading,
  pricingError,
  hasCoverPhoto,
  condition,
  onApplyTier,
  isGreedy,
  paymentsEnabled,
}: Props) {
  const { t } = useTranslation()
  const [methodOpen, setMethodOpen] = useState(false)
  const selectedTier = useMemo(() => tierFromPrice(price, suggestion), [price, suggestion])

  const askField = (
    <label className="builder-price-step__ask">
      <span className="builder-price-step__ask-label">{t('listing.builder.askingPriceLabel')}</span>
      <div className="builder-inflow-row builder-inflow-row--compact">
        <span className="builder-inflow-row__prefix builder-inflow-row__prefix--currency" aria-hidden>
          €
        </span>
        <div className="builder-inflow-row__slot">
          <input
            type="number"
            step="0.01"
            inputMode="decimal"
            className="builder-inflow-row__input"
            value={price}
            onChange={(e) => onPriceChange(e.target.value)}
          />
        </div>
      </div>
    </label>
  )

  const pageHead = (
    <BuilderStepHead
      title={t('listing.builder.priceSectionTitle')}
      lead={t('listing.builder.priceSectionLead')}
    />
  )

  if (isBasicPhone) {
    return (
      <div className="builder-price-step builder-price-step--basic builder-step-surface builder-step-surface--flat">
        {pageHead}
        <p className="builder-price-step__note">{t('listing.basicPhoneAiNote')}</p>
        {askField}
        {paymentsEnabled ? <SellerPayoutPreview price={price} /> : null}
      </div>
    )
  }

  const notices: string[] = []
  if (!hasCoverPhoto) notices.push(t('listing.photosGatePricing'))
  if (!condition) notices.push(t('listing.conditionSkippedPricingNote'))

  return (
    <div className="builder-price-step builder-step-surface builder-step-surface--flat">
      {pageHead}

      <section className="builder-price-step__ai" aria-labelledby="builder-price-ai-title">
        <div className="builder-price-step__ai-head">
          <div className="builder-price-step__ai-title-row">
            <span className="builder-price-step__ai-icon">
              <IconSparkles />
            </span>
            <h4 id="builder-price-ai-title" className="builder-price-step__ai-title">
              {t('pricing.aiTitle')}
            </h4>
          </div>
          <button
            type="button"
            className="builder-price-step__method-btn"
            aria-expanded={methodOpen}
            onClick={() => setMethodOpen((o) => !o)}
          >
            <IconInfo />
            {t('listing.builder.pricingHowLink')}
          </button>
        </div>

        {methodOpen ? <p className="builder-price-step__method-body">{t('pricing.methodNote')}</p> : null}

        {notices.length > 0 ? (
          <ul className="builder-price-step__notices">
            {notices.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        ) : null}

        {pricingLoading ? (
          <p className="builder-price-step__status">{t('listing.pricingCalculating', 'Calculating…')}</p>
        ) : pricingError ? (
          <p className="builder-price-step__status builder-price-step__status--error">{pricingError}</p>
        ) : suggestion ? (
          <div className="builder-price-step__tiers" role="radiogroup" aria-label={t('pricing.aiTitle')}>
            {(
              [
                {
                  tier: 'quick' as const,
                  label: t('pricing.tierQuick'),
                  desc: t('listing.builder.tierQuickDesc'),
                  amount: suggestion.quick_sale_eur,
                },
                {
                  tier: 'recommended' as const,
                  label: t('pricing.tierBalanced'),
                  desc: t('listing.builder.tierBalancedDesc'),
                  amount: suggestion.recommended_eur,
                  badge: t('pricing.topPick'),
                },
                {
                  tier: 'patient' as const,
                  label: t('pricing.tierPatient'),
                  desc: t('listing.builder.tierPatientDesc'),
                  amount: suggestion.patient_eur,
                },
              ] as const
            ).map((opt) => {
              const { tier, label, desc, amount } = opt
              const badge = 'badge' in opt ? opt.badge : undefined
              const selected = selectedTier === tier
              return (
                <button
                  key={tier}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  className={`builder-price-tier${selected ? ' builder-price-tier--selected' : ''}${
                    badge ? ' builder-price-tier--featured' : ''
                  }`}
                  onClick={() => onApplyTier(tier)}
                >
                  {badge ? <span className="builder-price-tier__badge">{badge}</span> : null}
                  <span className={`builder-price-tier__radio${selected ? ' builder-price-tier__radio--on' : ''}`} aria-hidden />
                  <span className="builder-price-tier__title-row">
                    <span className="builder-price-tier__title">{label}</span>
                  </span>
                  <span className="builder-price-tier__eur">{formatEur(amount)}</span>
                  <span className="builder-price-tier__desc">{desc}</span>
                </button>
              )
            })}
          </div>
        ) : (
          <p className="builder-price-step__status">{t('listing.pricingCalculating', 'Calculating…')}</p>
        )}

        {askField}

        {isGreedy ? (
          <div className="pricing-greedy-warning builder-price-step__greedy">
            <strong>{t('pricing.greedyTitle')}</strong>
            <p>{t('pricing.greedyBody')}</p>
          </div>
        ) : null}
        {paymentsEnabled ? <SellerPayoutPreview price={price} /> : null}
      </section>
    </div>
  )
}
